import { useState, useEffect } from 'react';
import { api } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Skeleton } from '../components/ui/skeleton';
import { Trophy, Medal, Award, Ticket } from 'lucide-react';

export default function RankingPage() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [ranking, setRanking] = useState([]);
  const [raffle, setRaffle] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const raffleData = await api.getActiveRaffle();
        setRaffle(raffleData);
        
        if (raffleData?.id) {
          const rankingData = await api.getRanking(raffleData.id);
          setRanking(rankingData);
        }
      } catch (error) {
        console.error('Error fetching ranking:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const getPositionIcon = (position) => {
    switch (position) {
      case 1:
        return <Trophy className="w-6 h-6 text-amber-500" />;
      case 2:
        return <Medal className="w-6 h-6 text-slate-400" />;
      case 3:
        return <Award className="w-6 h-6 text-amber-700" />;
      default:
        return <span className="w-6 h-6 flex items-center justify-center font-bold text-slate-400">{position}</span>;
    }
  };

  const getPositionStyle = (position) => {
    switch (position) {
      case 1:
        return 'bg-gradient-to-r from-amber-50 to-amber-100 border-amber-200';
      case 2:
        return 'bg-gradient-to-r from-slate-50 to-slate-100 border-slate-200';
      case 3:
        return 'bg-gradient-to-r from-orange-50 to-orange-100 border-orange-200';
      default:
        return 'bg-white border-slate-100';
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-CO', {
      style: 'currency',
      currency: 'COP',
      minimumFractionDigits: 0,
    }).format(amount);
  };

  if (loading) {
    return (
      <div className="page-container pt-6 space-y-4">
        <Skeleton className="h-10 w-48" />
        <Skeleton className="h-24 w-full rounded-2xl" />
        <Skeleton className="h-20 w-full rounded-2xl" />
        <Skeleton className="h-20 w-full rounded-2xl" />
      </div>
    );
  }

  return (
    <div className="page-container pt-6 space-y-6">
      <h1 className="heading-2">Ranking</h1>

      {!raffle ? (
        <Card className="card-base text-center py-12" data-testid="no-raffle">
          <Trophy className="w-16 h-16 mx-auto mb-4 text-muted-foreground" />
          <h2 className="heading-4 mb-2">No hay sorteo activo</h2>
          <p className="text-muted-foreground">
            El ranking se mostrará cuando haya un sorteo en curso
          </p>
        </Card>
      ) : (
        <>
          {/* Prizes Info */}
          <Card className="card-base bg-gradient-to-r from-cyan-50 to-blue-50" data-testid="prizes-info">
            <CardContent className="p-4">
              <h3 className="font-semibold mb-3">Premios por volumen de compra</h3>
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Trophy className="w-5 h-5 text-amber-500" />
                    <span>Mayor comprador</span>
                  </div>
                  <span className="font-bold text-primary">{formatCurrency(100000)}</span>
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Medal className="w-5 h-5 text-slate-400" />
                    <span>Segundo lugar</span>
                  </div>
                  <span className="font-bold text-primary">{formatCurrency(50000)}</span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Ranking List */}
          {ranking.length === 0 ? (
            <Card className="card-base text-center py-8">
              <Ticket className="w-12 h-12 mx-auto mb-3 text-muted-foreground" />
              <p className="text-muted-foreground">
                Aún no hay compradores en este sorteo
              </p>
            </Card>
          ) : (
            <div className="space-y-3">
              {ranking.map((entry) => (
                <Card 
                  key={entry.user_id}
                  className={`border-2 transition-all ${getPositionStyle(entry.position)} ${
                    entry.user_id === user?.id ? 'ring-2 ring-primary' : ''
                  }`}
                  data-testid={`ranking-${entry.position}`}
                >
                  <CardContent className="p-4 flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 rounded-full bg-white shadow-sm flex items-center justify-center">
                        {getPositionIcon(entry.position)}
                      </div>
                      <div>
                        <p className="font-medium">
                          {entry.user_name}
                          {entry.user_id === user?.id && (
                            <span className="ml-2 text-xs bg-primary/10 text-primary px-2 py-1 rounded-full">
                              Tú
                            </span>
                          )}
                        </p>
                        <p className="text-sm text-muted-foreground">
                          {entry.tickets_count} tickets comprados
                        </p>
                      </div>
                    </div>
                    {entry.position <= 2 && (
                      <div className="text-right">
                        <p className="text-xs text-muted-foreground">Premio</p>
                        <p className="font-bold text-success">
                          {formatCurrency(entry.position === 1 ? 100000 : 50000)}
                        </p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          )}

          {/* Current Raffle Info */}
          <Card className="card-base">
            <CardHeader className="pb-3">
              <CardTitle className="text-base">{raffle.name}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Tickets vendidos</span>
                <span className="font-medium">{raffle.sold_tickets} / {raffle.total_tickets}</span>
              </div>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}
